import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { motion } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import FormatDateTime from '../../shared/utils/dateUtils';
import FormatPrice from '../properties/components/shared/PriceFormatter';

const token = Cookies.get('jwt');

const assetTypes = [
  { label: 'ที่ดิน', value: 'land' },
  { label: 'บ้าน', value: 'house' },
  { label: 'ทาวน์เฮาส์', value: 'townhouse' },
  { label: 'พาณิชย์', value: 'commercial' },
  { label: 'คอนโด', value: 'condo' },
  { label: 'อื่น ๆ', value: 'other' },
];

export default function CartSystem() {
  const session_id = Cookies.get('jwt');
  const [dataCarts, setDataCarts] = useState([]);
  const [tempDisplayRemoveCartPost, setTempDisplayRemoveCartPost] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (session_id === undefined) {
      window.location.replace('/login');
    } else {
      getDataCarts(page, startDate, endDate, selectedTypes);
    }

    const savedPosition = Cookies.get('scrollPosition');
    if (savedPosition) {
      window.scrollTo(0, parseInt(savedPosition, 10));
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScroll = () => {
    const position = window.pageYOffset;
    Cookies.set('scrollPosition', position);
  };

  const handleRemoveAsset = (postId) => {
    Swal.fire({
      title: 'ต้องการลบทรัพย์นี้ออกจากทรัพย์ที่ถูกใจ ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'red',
      cancelButtonColor: 'dark',
      confirmButtonText: 'ยืนยัน!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${process.env.REACT_APP_API}/event/removeBasket`, {
          data: { assetId: postId },
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(async (res) => {
            setTempDisplayRemoveCartPost(postId);
            setPage(1);
            getDataCarts(1, startDate, endDate, selectedTypes);
            await Swal.fire('ลบเรียบร้อย!', `คุณได้ลบ ${postId}. ออกจากรายการทรัพย์ที่สนใจแล้ว`, 'success');
          })
          .catch((err) => {
            Swal.fire('ไม่สำเร็จ!', `${err.response.data.Error}`, 'failed');
          });
      }
    });
  };

  const getDataCarts = async (page, startDate, endDate, types) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/event/getBasket`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, startDate, endDate, types: types.join(',') },
      });
      if (page === 1) {
        setDataCarts(response.data.assets);
      } else {
        setDataCarts((prevData) => [...prevData, ...response.data.assets]);
      }
      setHasMore(response.data.assets.length > 0);
    } catch (error) {
      console.error('Error fetching basket data:', error);
    }
  };

  const handleLoadMore = () => {
    if (hasMore) {
      const newPage = page + 1;
      setPage(newPage);
      getDataCarts(newPage, startDate, endDate, selectedTypes);
    }
  };

  const handleFilterChange = () => {
    setPage(1);
    getDataCarts(1, startDate, endDate, selectedTypes);
  };

  const handleTypeChange = (type) => {
    setSelectedTypes((prevTypes) => {
      if (prevTypes.includes(type)) {
        return prevTypes.filter((t) => t !== type);
      } else {
        return [...prevTypes, type];
      }
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const applyFilterAndCloseModal = () => {
    handleFilterChange();
    closeModal();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header Section */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-4">ทรัพย์ที่ถูกใจ</h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              รวบรวมทรัพย์สินที่คุณสนใจไว้ในที่เดียว เพื่อความสะดวกในการเปรียบเทียบและตัดสินใจ
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-gray-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
              </svg>
              <span>{dataCarts.length} รายการ</span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filter Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-12"
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-black mb-4">กรองข้อมูล</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      วันที่เริ่มต้น
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      วันที่สิ้นสุด
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3 md:min-w-[200px]">
                <button
                  onClick={openModal}
                  className="px-6 py-3 bg-white border-2 border-black text-black rounded-xl hover:bg-black hover:text-white transition-all duration-200 font-medium"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    เลือกประเภท
                  </span>
                </button>
                <button
                  onClick={handleFilterChange}
                  className="px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    ค้นหา
                  </span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Property Grid */}
        {dataCarts.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {dataCarts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`group relative bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 ${tempDisplayRemoveCartPost === post.id ? 'hidden' : ''}`}
                style={{ opacity: post.sold || post.ban ? 0.5 : 1 }}
              >
                {post.sold || post.ban && (
                  <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-20">
                    <div className="text-center p-4">
                      <svg className="w-16 h-16 text-white mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-white text-base font-bold">ทรัพย์นี้ได้ทำการลบ<br />หรือขายออกไปแล้ว</p>
                    </div>
                  </div>
                )}
                <div className="relative w-full h-64 overflow-hidden">
                  <Link to={post.status === false ? `/product/${post.id}` : '/home'}>
                    <img
                      src={post.images_url.AssetImagesURL[0]}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      alt="Property"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </Link>
                  <button
                    className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-red-500 hover:text-white transition-all duration-200 flex items-center justify-center z-10 group/btn"
                    onClick={(event) => { event.stopPropagation(); handleRemoveAsset(post.id); }}
                  >
                    <svg className="w-5 h-5 text-black group-hover/btn:text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <span className="absolute top-4 left-4 bg-black text-white text-xs px-3 py-1.5 rounded-full font-medium">
                    #{post.id}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <time className="text-xs text-gray-500 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      <FormatDateTime isoDateString={post.date} />
                    </time>
                  </div>
                  <Link to={post.status === false ? `/product/${post.id}` : '/home'}>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2 line-clamp-1 group-hover:text-gray-700 transition-colors">
                        {post.title}
                      </h3>
                      <h4 className="text-2xl font-bold text-black mb-3">
                        <FormatPrice number={post.sell_price == null ? '' : post.sell_price} suffix={`${post.sell_price == null ? '' : 'บาท'}`} />
                      </h4>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4 leading-relaxed">
                        {post.description}
                      </p>
                      <div className="flex gap-4 pt-4 border-t border-gray-200">
                        {post.rooms.Bedroom && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <img src="https://cdn-icons-png.flaticon.com/512/2642/2642252.png" alt="Bedroom" className="w-5 h-5 opacity-70" />
                            <span className="text-sm font-medium">{post.rooms.Bedroom}</span>
                          </div>
                        )}
                        {post.rooms.Bathroom && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <img src="https://cdn-icons-png.flaticon.com/512/3565/3565091.png" alt="Bathroom" className="w-5 h-5 opacity-70" />
                            <span className="text-sm font-medium">{post.rooms.Bathroom}</span>
                          </div>
                        )}
                        {post.type && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <img src="https://cdn-icons-png.flaticon.com/512/4305/4305629.png" alt="Type" className="w-5 h-5 opacity-70" />
                            <span className="text-sm font-medium capitalize">{post.type}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ยังไม่มีทรัพย์ที่ถูกใจ</h3>
            <p className="text-gray-600">เริ่มต้นค้นหาและบันทึกทรัพย์ที่คุณสนใจกันเลย</p>
          </motion.div>
        )}

        {hasMore && (
          <div className="flex justify-center mt-12">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLoadMore}
              className="px-10 py-4 bg-black text-white rounded-xl hover:bg-gray-800 transition-all duration-200 font-medium shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              โหลดเพิ่มเติม
            </motion.button>
          </div>
        )}
      </div>

      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={closeModal}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md p-8 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl border border-gray-200">
                <Dialog.Title as="h3" className="text-2xl font-bold text-black mb-6">
                  เลือกประเภททรัพย์สิน
                </Dialog.Title>
                <div className="space-y-3">
                  {assetTypes.map((type) => (
                    <label key={type.value} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        value={type.value}
                        checked={selectedTypes.includes(type.value)}
                        onChange={() => handleTypeChange(type.value)}
                        className="w-5 h-5 text-black border-gray-300 rounded focus:ring-black focus:ring-2"
                      />
                      <span className="text-gray-800 font-medium">{type.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-8 flex gap-3">
                  <button
                    type="button"
                    className="flex-1 px-6 py-3 text-white bg-black rounded-xl hover:bg-gray-800 transition-all duration-200 font-medium shadow-sm"
                    onClick={applyFilterAndCloseModal}
                  >
                    ตกลง
                  </button>
                  <button
                    type="button"
                    className="flex-1 px-6 py-3 text-black bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                    onClick={closeModal}
                  >
                    ยกเลิก
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
